import { Link, useLocation } from "react-router-dom";
import { Sparkles, MessageCircle, Settings } from "lucide-react";
import { useMobile } from "@/hooks/use-mobile";
import { useTabNavigation } from "@/api-frontend/handleTabNavigation";
import { useTabBadges } from "@/api-frontend/updateTabBadges";
import { TabManager, NavigationUtils } from "@/lib/MobileNavigation";
import { Badge } from "@/components/ui/badge";

export function BottomNavigation() {
  const location = useLocation();
  const isMobile = useMobile();
  
  const { getCurrentTabId } = useTabNavigation();
  const { badges, formatBadge, hasBadge } = useTabBadges();
  
  // Get current active tab
  const activeTabId = getCurrentTabId();
  
  // Create navigation items using TabManager
  const tabItems = TabManager.createTabItems(activeTabId, badges);
  
  // Map icon names to actual icon components
  const iconMap = {
    Sparkles,
    MessageCircle,
    Settings,
  } as const;

  const navItems = tabItems.map(tab => ({
    ...tab,
    icon: iconMap[tab.icon as keyof typeof iconMap] || Sparkles,
  }));

  // Only show on mobile and check if navigation should be visible
  if (!isMobile || !NavigationUtils.shouldShowNavigation(location.pathname)) {
    return null;
  }

  return (
    <nav 
      className="fixed bottom-0 left-0 right-0 bg-card border-t border-border z-50"
      data-testid="bottom-navigation"
    >
      <div className="flex items-center justify-around py-2 px-4 max-w-md mx-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const showBadge = hasBadge(item.id);
          const badgeText = formatBadge(item.badge);
          
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`relative flex flex-col items-center justify-center py-2 px-3 rounded-lg transition-colors min-w-0 flex-1 ${
                item.isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
              data-testid={TabManager.getTestId(item.id)}
              aria-label={TabManager.getAccessibilityLabel(item.id)}
            >
              <div className="relative">
                <Icon className="h-5 w-5 mb-1" />
                {showBadge && (
                  <Badge 
                    variant={item.badge === '!' ? 'destructive' : 'default'}
                    className="absolute -top-1 -right-1 min-w-[16px] h-4 text-[10px] px-1 py-0 rounded-full flex items-center justify-center"
                    data-testid={`${TabManager.getTestId(item.id)}-badge`}
                  >
                    {badgeText}
                  </Badge>
                )}
              </div>
              <span className="text-xs font-medium truncate">{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

export default BottomNavigation;